import React, { Component } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import Notification from '../Notification/Notification';

const bem = new BEMHelper('Tooltip');

const propTypes = {
    defaultOpen: PropTypes.bool,
    open: PropTypes.bool,
    arrowMargin: PropTypes.number,
    className: PropTypes.string,
    children: PropTypes.node,
    getArrowDimensions: PropTypes.func,
    notificationClassName: PropTypes.string,
    childWrapperClassName: PropTypes.string,
};

const defaultProps = {
    defaultOpen: true,
    arrowMargin: 22,
};

export default class Tooltip extends Component {
    constructor(props) {
        super(props);

        this.targetRef = React.createRef();
        this.notificationRef = React.createRef();

        this.state = {
            open: props.defaultOpen,
        };
    }

    componentDidMount() {
        this.listener = document.addEventListener('click', () => this.close());

        if (this.targetRef.current && this.notificationRef.current) {
            const getArrowDimensions = this.props.getArrowDimensions || this.getArrowDimensions;

            const dimensions = getArrowDimensions(this.targetRef.current, this.notificationRef.current, this.props);
            this.setState(dimensions);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.targetRef.current && this.notificationRef.current) {
            const getArrowDimensions = this.props.getArrowDimensions || this.getArrowDimensions;

            const { bottom, left, width } = getArrowDimensions(this.targetRef.current, this.notificationRef.current, this.props);

            if (left !== prevState.left || width !== prevState.width || bottom !== prevState.bottom) {
                this.setState({ left, width, bottom });
            }
        }
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.listener);
    }

    // eslint-disable-next-line class-methods-use-this
    getArrowDimensions(target, notification, props) {
        const { arrowMargin } = props;

        // wrapped component, refers to (`childWrapper` within jsx of render)
        const targetRect = target.getBoundingClientRect();
        const tooltipRect = notification.getBoundingClientRect();

        const left = Math.min(
            // Notification smaller than half width of wrapped component, place arrow at end of Notification
            tooltipRect.width - arrowMargin / 2,
            Math.max(
                // Arrow wider than wrapped component, place arrow at start of Notification
                arrowMargin / 2,
                // Place arrow to point to center of wrapped component
                targetRect.left + targetRect.width / 2 - tooltipRect.left,
            ),
        );
        // Set width of Notification same as wrapped component
        const { width } = targetRect;

        return { left, width };
    }

    open() {
        this.setState({ open: true });
    }

    close() {
        this.setState({ open: false });
    }

    /**
     * {defaultOpen} is destructured to avoid sending it down to the DOM,
     * as notification uses the spread operator in its props
     */
    render() {
        const {
            arrowMargin,
            children,
            childWrapperClassName,
            className,
            defaultOpen,
            getArrowDimensions,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            notificationClassName,
            ...rest
        } = this.props;
        const { left, width } = this.state;

        const applyWidthOverride = typeof notificationClassName === 'string' && !!notificationClassName;
        const minWidthStyle = width && !applyWidthOverride && { width };
        const leftStyle = getArrowDimensions && { left: `-${left - arrowMargin}px` };

        const open = typeof this.props.open === 'undefined' ? this.state.open : this.props.open;
        return (
            <div className={bem(null, null, className)}>
                <span className={bem('childWrapper', childWrapperClassName)} ref={this.targetRef}>
                    {children}
                </span>
                {open && (
                    <div className={bem('notification', null, notificationClassName)} data-testid="notification" style={{ ...minWidthStyle, ...leftStyle }}>
                        <Notification arrow="bottom" arrowStyle={left ? { left } : null} forwardedRef={this.notificationRef} {...rest} />
                    </div>
                )}
            </div>
        );
    }
}

Tooltip.propTypes = propTypes;
Tooltip.defaultProps = defaultProps;
